import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgSelectModule } from '@ng-select/ng-select';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { AccessListTypeEnum, ShieldAccessListsApiStore, ShieldApiStore, ShieldCreateEditAccessListModal, ShieldUpgradeModalComponent } from '@bn/app-shield';
import { PlanType } from '@bn/app-shield/enums';
import { SharedEntitiesModule } from '@bn/shared/entities';
import { AbstractLazyDirective } from '@bn/shared/entities/abstracts/abstract-lazy.directive';
import { ClickActionEvent, StringTMap } from '@bn/shared/entities/index';
import { StoreRequest } from '@bn/shared/state-store';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { ConfirmationDialogComponent } from '@bn/web-ui/dialog';
import { DropdownMenuItem } from '@bn/web-ui/dropdown-menu';
import { DropdownMenuComponent } from '@bn/web-ui/dropdown-menu/dropdown-menu.component';
import { SelectItem } from '@bn/web-ui/select/models';
import { SwitchComponent } from '@bn/web-ui/switch/switch.component';
import { AccessListAction, AccessListDetails } from '../../openapi';

@Component({
  selector: 'app-shield-access-lists-threat-item',
  templateUrl: './app-shield-access-lists-threat-item.component.html',
  styleUrls: ['./app-shield-access-lists-threat-item.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    SharedEntitiesModule,
    ButtonComponent,
    DropdownMenuComponent,
    SwitchComponent,
    FaIconComponent,
    NgSelectModule,
    NgxSkeletonLoaderComponent,
    TooltipDirective,
  ],
})
export class AppShieldAccessListsThreatItemComponent extends AbstractLazyDirective {
  private readonly dialog = inject(BsModalService);

  readonly #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #shieldAccessListsStore = inject(ShieldAccessListsApiStore);
  readonly #shieldStore = inject(ShieldApiStore);
  readonly selectedShield = this.#shieldStore.selectedEntity();
  readonly isInitializing = this.#shieldAccessListsStore.isLoading(StoreRequest.List);
  readonly isUpdating = this.#shieldAccessListsStore.isLoading(StoreRequest.Update);
  readonly isDeleted = this.#shieldAccessListsStore.isFinished(StoreRequest.Delete);
  readonly enums = this.#shieldAccessListsStore.enums;

  isUpgradeModalOpen = false;
  deleteDialogInstance?: BsModalRef<ConfirmationDialogComponent>;
  dropdownItems: DropdownMenuItem[] = [
    {
      label: 'Edit',
      icon: 'pencil',
      action: 'edit',
    },
    { type: 'divider' },
    {
      type: 'link',
      label: 'Delete list',
      action: 'delete',
      icon: 'trash-can',
    },
  ];

  protected readonly PlanType = PlanType;
  protected readonly AccessListTypeEnum = AccessListTypeEnum;

  @Input()
  public item?: AccessListDetails;

  @Input()
  public listKey?: 'managedLists' | 'customLists';

  @Input()
  public isSkeleton? = false;

  @Input()
  public showUpgradeTooltips? = false;

  threadLists = null;
  switchOnOff: boolean | null = null;
  actionType: SelectItem | null = null;
  accessListAction = AccessListAction;

  availableActions: SelectItem[] = [
    {
      Id: AccessListAction.Bypass,
      Name: 'Bypass',
    },
    {
      Id: AccessListAction.Allow,
      Name: 'Allow',
    },
    {
      Id: AccessListAction.Block,
      Name: 'Block',
    },
    {
      Id: AccessListAction.Challenge,
      Name: 'Challenge',
    },
    {
      Id: AccessListAction.Log,
      Name: 'Log',
    },
  ];

  onSwitchClick(isEnabled: boolean): void {
    const userPlan = this.selectedShield()?.planType ?? -1;
    const requiredPlan = this.item?.requiredPlan ?? -1;
    const shieldZoneId = this.selectedShield()?.shieldZoneId;

    if (!shieldZoneId) return;

    if (userPlan < requiredPlan) {
      if (!this.isUpgradeModalOpen) this.showUpgradeModal(isEnabled, this.item?.requiredPlan);
    } else {
      this.handleListUpdate(isEnabled);
    }
  }

  handleListUpdate(isEnabled: boolean): void {
    if (!this.item?.configurationId || !this.listKey) return;

    this.#shieldAccessListsStore.updateAccessList({
      id: this.item.configurationId,
      shieldZoneId: Number(this.#shieldStore.selectedEntityId()()),
      listKey: this.listKey,
      data: {
        isEnabled,
      },
    });
  }

  onActionSelect(action: AccessListAction): void {
    if (!this.item?.listId || !this.listKey) return;

    this.#shieldAccessListsStore.updateAccessList({
      id: this.item.configurationId,
      shieldZoneId: Number(this.#shieldStore.selectedEntityId()()),
      listKey: this.listKey,
      data: {
        action,
      },
    });
  }

  onDropdownClick({ action }: DropdownMenuItem, id?: number): void {
    if (!id) return;

    if (action === 'edit') {
      this.onEditListClick(id);
    }
    if (action === 'delete') {
      this.deleteList(id);
    }
  }

  onEditListClick(id: number): void {
    this.dialog.show(ShieldCreateEditAccessListModal, {
      class: 'modal-dialog-centered modal-lg',
      id: 'shieldAddEditModal',
      ignoreBackdropClick: true,
      initialState: {
        data: {
          extra: {
            id,
            icon: ['fad', 'c'],
            isEdit: true,
          },
        },
      },
    });
  }

  deleteList(id: number): void {
    this.deleteDialogInstance = this.dialog.show(ConfirmationDialogComponent, {
      class: 'modal-dialog-centered modal-sm',
      initialState: {
        data: {
          contextType: 'danger',
          confirmButtonName: 'Delete',
          description: `This action cannot be undone. All rules using this list will need to be updated.`,
          event: {
            action: 'confirm',
          },
          title: 'Are you sure you want to delete this list?',
          textCenter: true,
        },
      },
    });

    this.subscriptions.add(
      this.deleteDialogInstance.content?.getOnAction$().subscribe((event: ClickActionEvent) => {
        if (event.action === 'cancel') {
          this.dialog.hide();
        }
        if (event.action === 'confirm') {
          this.#shieldAccessListsStore.deleteAccessList({ id, shieldZoneId: Number(this.#shieldStore.selectedEntityId()()) });
        }
      }),
    );
  }

  subscribeToStoreSignals(): void {
    toObservable(this.isDeleted)
      .pipe(takeUntilDestroyed())
      .subscribe((isDeleted) => {
        if (isDeleted) {
          this.deleteDialogInstance?.hide();
        }
      });
  }

  showUpgradeModal(isEnabled: boolean, requiredPlan?: PlanType): void {
    const dialog = this.dialog.show(ShieldUpgradeModalComponent, {
      class: 'modal-sm modal-md-xl modal-dialog-centered',
      initialState: {
        data: {
          title: 'Keep the good traffic.<br/> Block the known threats.',
          extra: {
            requiredPlan: requiredPlan ?? PlanType.Advanced,
            title: 'Upgrade to Unlock Curated Threat Lists.',
            description: `Your current plan includes limited access to Custom Access Lists, but only one list and up to 1,000 entries. That means your ability to block or allow specific traffic sources is constrained.<br/><br/>Bunny Shield ${requiredPlan ? PlanType[requiredPlan] : PlanType[PlanType.Advanced]} gives you the full toolkit for proactive, large-scale access management.`,
            imageSrc: '/assets/web-ui/images/bunny-shield.svg',
            itemsList: [
              {
                icon: 'circle-check',
                text: 'Increased Limits: Create multiple Custom Access Lists with expanded capacity for IPs, CIDRs, ASNs, countries, organizations, and JA4 fingerprints',
                iconContextType: 'dark',
              },
              {
                icon: 'circle-check',
                text: 'Curated Threat Lists: Instantly block known botnets, scrapers, VPNs, Tor exit nodes, malicious datacenters and more.',
                iconContextType: 'dark',
              },
            ],
          },
        },
      },
    });

    this.subscriptions.add(
      dialog.content?.getOnAction$().subscribe((event: ClickActionEvent) => {
        if (event.action === 'confirm') {
          this.handleListUpdate(isEnabled);
        } else {
          this.dialog.hide();

          const isSuccessScreen = (event.data?.entity as StringTMap<boolean>)?.isSuccessScreen;

          if (isSuccessScreen) return;

          // Note: this is hack because of switch component and ngModel
          if (!this.item) return;

          this.item = {
            ...this.item,
            isEnabled: true,
          };

          setTimeout(() => {
            if (!this.item) return;

            this.item = {
              ...this.item,
              isEnabled: false,
            };

            this.#changeDetectorRef.markForCheck();
          }, 0);

          this.#changeDetectorRef.markForCheck();
        }
      }),
    );

    this.subscriptions.add(
      dialog.onHide?.subscribe(() => {
        this.isUpgradeModalOpen = false;

        this.dialog.hide();
      }),
    );

    this.isUpgradeModalOpen = true;
  }
}
